import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Building2, Clock, AlertTriangle, CheckCircle2,
  XCircle, Timer, Users, ShieldAlert, FileWarning, ChevronRight,
  Filter, BarChart3, MessageSquareWarning, Inbox,
} from 'lucide-react';
import { mockProviders, mockProviderLogs, mockProviderAudit } from '@/data/mockProviderData';

/* ──────── BADGE HELPERS ──────── */
function EsitoBadge({ esito }) {
  const cfg = {
    Accettato: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    Ricevuto: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    Rifiutato: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    Scaduto: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  };
  const c = cfg[esito] || cfg.Ricevuto;
  return <Badge className={`${c.bg} ${c.text} border ${c.border} text-[10px] font-semibold`}>{esito}</Badge>;
}

function OrigineBadge({ origine }) {
  const cfg = {
    'Preferenza Paziente': { bg: 'bg-blue-50', text: 'text-blue-700' },
    'Job Rotation': { bg: 'bg-violet-50', text: 'text-violet-700' },
    'Broadcast': { bg: 'bg-slate-100', text: 'text-slate-700' },
  };
  const c = cfg[origine] || cfg.Broadcast;
  return <Badge variant="outline" className={`${c.bg} ${c.text} border-0 text-[10px]`}>{origine}</Badge>;
}

/* ════════════════════════════════════════════
   MASTER VIEW — Dashboard Generale
   ════════════════════════════════════════════ */
function MasterView({ providers, onSelect }) {
  const [filtroTempo, setFiltroTempo] = useState('30gg');
  const [filtroEsito, setFiltroEsito] = useState('tutti');
  const [filtroComplessita, setFiltroComplessita] = useState('tutte');

  const filtered = useMemo(() => {
    let list = [...providers];
    if (filtroEsito === 'critici') list = list.filter((p) => p.rifiutiPerc > 15 || p.indiceReclami > 2);
    if (filtroComplessita === 'alta') list = list.filter((p) => p.tassoAccettazione < 80);
    if (filtroComplessita === 'bassa') list = list.filter((p) => p.tassoAccettazione >= 90);
    return list;
  }, [providers, filtroEsito, filtroComplessita]);

  return (
    <div className="flex flex-col gap-6" data-testid="provider-master-view">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-foreground" data-testid="page-title">Logistica Erogatori</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Cruscotto di monitoraggio performance provider accreditati</p>
      </div>

      {/* Filtri */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="filters-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filtri</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={filtroTempo}
              onChange={(e) => setFiltroTempo(e.target.value)}
              className="h-9 rounded-lg border border-border/60 bg-card px-3 text-xs focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none"
              data-testid="filter-tempo"
            >
              <option value="30gg">Ultimi 30 giorni</option>
              <option value="trimestre">Ultimo Trimestre</option>
              <option value="anno">Anno in corso</option>
            </select>
            <select
              value={filtroEsito}
              onChange={(e) => setFiltroEsito(e.target.value)}
              className="h-9 rounded-lg border border-border/60 bg-card px-3 text-xs focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none"
              data-testid="filter-esito"
            >
              <option value="tutti">Tutti gli esiti</option>
              <option value="critici">Solo Rifiuti / Scaduti</option>
            </select>
            <select
              value={filtroComplessita}
              onChange={(e) => setFiltroComplessita(e.target.value)}
              className="h-9 rounded-lg border border-border/60 bg-card px-3 text-xs focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none"
              data-testid="filter-complessita"
            >
              <option value="tutte">Tutte le complessita</option>
              <option value="bassa">Performance Alta (Acc. 90%+)</option>
              <option value="alta">Performance Critica (Acc. &lt;80%)</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Tabella KPI */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="kpi-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="kpi-table">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="px-5 py-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Erogatore</th>
                  <th className="px-4 py-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Tasso Acc.</th>
                  <th className="px-4 py-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Rifiuti / Timeout</th>
                  <th className="px-4 py-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Tempo SLA</th>
                  <th className="px-4 py-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Freq. Scelta</th>
                  <th className="px-4 py-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider text-center">Reclami</th>
                  <th className="px-3 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((prov) => {
                  const accColor = prov.tassoAccettazione >= 90 ? 'text-emerald-600 bg-emerald-50' : prov.tassoAccettazione >= 80 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
                  const recColor = prov.indiceReclami === 0 ? 'text-emerald-600 bg-emerald-50' : prov.indiceReclami <= 2 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
                  return (
                    <tr
                      key={prov.id}
                      onClick={() => onSelect(prov)}
                      className="border-b border-border/20 hover:bg-muted/30 cursor-pointer transition-colors"
                      data-testid={`provider-row-${prov.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-violet-500" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{prov.nome}</p>
                            <p className="text-[10px] text-muted-foreground">{prov.zona}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Badge className={`${accColor} border-0 text-xs font-bold`}>{prov.tassoAccettazione}%</Badge>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-xs font-semibold text-red-600">{prov.rifiuti}</span>
                        <span className="text-[10px] text-muted-foreground ml-1">({prov.rifiutiPerc}%)</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-xs font-medium text-foreground">{prov.tempoMedioSLA}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-xs font-semibold text-foreground">{prov.frequenzaScelta}</span>
                        <span className="text-[10px] text-muted-foreground ml-1">volte</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Badge className={`${recColor} border-0 text-xs font-bold`}>{prov.indiceReclami}</Badge>
                      </td>
                      <td className="px-3 py-4">
                        <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-muted-foreground">Nessun erogatore corrisponde ai filtri selezionati.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ════════════════════════════════════════════
   DETAIL VIEW — Scheda Erogatore
   ════════════════════════════════════════════ */
function DetailView({ provider, onBack }) {
  const [activeTab, setActiveTab] = useState('log');
  const logs = mockProviderLogs[provider.id] || [];
  const audit = mockProviderAudit[provider.id] || { reclami: [], segnalazioni: [] };
  const totalAudit = audit.reclami.length + audit.segnalazioni.length;

  const kpis = [
    { label: 'Tasso Accettazione', value: `${provider.tassoAccettazione}%`, color: provider.tassoAccettazione >= 90 ? 'text-emerald-600' : provider.tassoAccettazione >= 80 ? 'text-amber-600' : 'text-red-600', icon: CheckCircle2 },
    { label: 'Rifiuti / Timeout', value: `${provider.rifiuti} (${provider.rifiutiPerc}%)`, color: 'text-red-600', icon: XCircle },
    { label: 'Tempo Medio SLA', value: provider.tempoMedioSLA, color: 'text-foreground', icon: Timer },
    { label: 'Frequenza Scelta', value: `${provider.frequenzaScelta} volte`, color: 'text-foreground', icon: Users },
    { label: 'Reclami Attivi', value: String(provider.indiceReclami), color: provider.indiceReclami === 0 ? 'text-emerald-600' : 'text-red-600', icon: ShieldAlert },
  ];

  return (
    <div className="flex flex-col gap-6" data-testid="provider-detail-view">
      {/* Back + Title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
          data-testid="detail-back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Torna alla lista
        </button>
      </div>

      {/* Header Card */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="detail-header">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-violet-500" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">{provider.nome}</h2>
                <p className="text-xs text-muted-foreground">{provider.tipo} — {provider.zona}</p>
                <div className="flex items-center gap-2 mt-1">
                  {provider.specialita.map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                  ))}
                </div>
              </div>
            </div>
            <Badge variant="outline" className="text-[10px]">{provider.contratto}</Badge>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {kpis.map((kpi) => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.label} className="p-3 rounded-xl bg-muted/20 border border-border/30">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">{kpi.label}</span>
                  </div>
                  <p className={`text-sm font-bold ${kpi.color}`}>{kpi.value}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tab Switcher */}
      <div className="flex gap-1 p-1 bg-muted/30 rounded-xl w-fit" data-testid="detail-tabs">
        <button
          onClick={() => setActiveTab('log')}
          className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'log' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-log"
        >
          <Inbox className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Registro Log ({logs.length})
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === 'audit' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-audit"
        >
          <MessageSquareWarning className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Audit e Feedback
          {totalAudit > 0 && <Badge className="ml-1.5 bg-red-100 text-red-700 border-0 text-[10px] px-1.5">{totalAudit}</Badge>}
        </button>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'log' && (
          <motion.div key="log" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <LogTable logs={logs} />
          </motion.div>
        )}
        {activeTab === 'audit' && (
          <motion.div key="audit" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <AuditPanel audit={audit} providerNome={provider.nome} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ──────── LOG TABLE ──────── */
function LogTable({ logs }) {
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="log-table-card">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="log-table">
            <thead>
              <tr className="border-b border-border/40">
                <th className="px-5 py-3.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">ID Paziente</th>
                <th className="px-4 py-3.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Data/Ora Invio</th>
                <th className="px-4 py-3.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Origine</th>
                <th className="px-4 py-3.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Esito</th>
                <th className="px-4 py-3.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Tempo Risposta</th>
                <th className="px-4 py-3.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Causale Rifiuto</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={i} className="border-b border-border/20 hover:bg-muted/20 transition-colors" data-testid={`log-row-${i}`}>
                  <td className="px-5 py-3.5">
                    <span className="text-xs font-bold text-violet-600">#{log.id}</span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-muted-foreground">{log.dataInvio}</td>
                  <td className="px-4 py-3.5"><OrigineBadge origine={log.origine} /></td>
                  <td className="px-4 py-3.5"><EsitoBadge esito={log.esito} /></td>
                  <td className="px-4 py-3.5 text-xs font-medium text-foreground">{log.tempoRisposta}</td>
                  <td className="px-4 py-3.5 text-xs text-muted-foreground max-w-[220px] truncate">{log.causaleRifiuto}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ──────── AUDIT PANEL ──────── */
function AuditPanel({ audit, providerNome }) {
  const hasReclami = audit.reclami.length > 0;
  const hasSegnalazioni = audit.segnalazioni.length > 0;
  const empty = !hasReclami && !hasSegnalazioni;

  return (
    <div className="flex flex-col gap-6" data-testid="audit-panel">
      {/* Reclami */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Reclami Formali ({audit.reclami.length})</span>
          </div>
          {hasReclami ? (
            <ul className="flex flex-col gap-4" data-testid="reclami-list">
              {audit.reclami.map((r) => (
                <li key={r.id} className="p-4 rounded-xl bg-red-50/50 border border-red-100" data-testid={`reclamo-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px] font-semibold">{r.id}</Badge>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">{r.data}</span>
                      <Badge variant="outline" className="text-[10px]">Paz. {r.paziente}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-foreground leading-relaxed">{r.testo}</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50/50 border border-emerald-100">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <p className="text-xs text-emerald-700">Nessun reclamo formale registrato per {providerNome}.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Segnalazioni Cambio Ente */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileWarning className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Richieste Cambio Ente ({audit.segnalazioni.length})</span>
          </div>
          {hasSegnalazioni ? (
            <ul className="flex flex-col gap-4" data-testid="segnalazioni-list">
              {audit.segnalazioni.map((s) => (
                <li key={s.id} className="p-4 rounded-xl bg-amber-50/50 border border-amber-100" data-testid={`segnalazione-${s.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] font-semibold">{s.id}</Badge>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">{s.data}</span>
                      <Badge variant="outline" className="text-[10px]">Paz. {s.paziente}</Badge>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-1">{s.tipo}</p>
                  <p className="text-xs text-foreground leading-relaxed">{s.motivo}</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50/50 border border-emerald-100">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <p className="text-xs text-emerald-700">Nessuna richiesta di cambio ente per {providerNome}.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {empty && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <BarChart3 className="w-8 h-8 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">Nessuna criticita rilevata per questo erogatore.</p>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   PAGE EXPORT — MASTER-DETAIL
   ════════════════════════════════════════════ */
export default function ProviderTrackerDashboard() {
  const [selectedProvider, setSelectedProvider] = useState(null);

  return (
    <AppLayout role="case-manager">
      <AnimatePresence mode="wait">
        {selectedProvider ? (
          <motion.div key="detail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <DetailView provider={selectedProvider} onBack={() => setSelectedProvider(null)} />
          </motion.div>
        ) : (
          <motion.div key="master" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
            <MasterView providers={mockProviders} onSelect={setSelectedProvider} />
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
